import { useMemo } from 'react';
import {
  Box, Button, Card, Chip, Stack, Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import type { GridColDef } from '@mui/x-data-grid';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter } from '../../components/data-grid/RouteDataGrid';
import type { ExportColumn } from '../../utils/export';
import { fmtDateTime } from '../../utils/format';
import { apiBaseUrl, TOKEN_STORAGE_KEY } from '../../auth/config';
import { apiFetch } from '../../api/client';
import { useAccessToken } from '../../auth/useAccessToken';
import type { MachineQrPairSummary } from '../../api/types';
import {
  useMachineQrBatchDetail,
  useMyPermissions,
} from '../../api/queries';
import { useAlert } from '../../components/dialogs/DialogProvider';

/**
 * Detail view for a single machine (cabinet) QR batch. Header card with
 * batch metadata + lifecycle status, grid of every QR pair in the batch.
 * Header-template snapshotting deliberately omitted at M6 — can be added
 * later if operators want branded cabinet stickers; until then cabinet
 * stickers are intentionally plain.
 */
export default function MachineQrBatchDetailPage() {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const alert = useAlert();

  const batchQ = useMachineQrBatchDetail(batchId ?? null);
  const getToken = useAccessToken();
  const perms = useMyPermissions();
  const canPrint = perms.data?.codes?.includes('route.machine-qr-batches.mint') ?? false;

  const batch = batchQ.data;
  usePageTitle(batch ? `Batch ${batch.code}` : 'Machine QR batch');

  const batchChipColour = (s: string) =>
    s === 'created' ? 'info'    :
    s === 'printed' ? 'success' :
    s === 'voided'  ? 'error'   :
    'default';

  const pairChipColour = (s: string) =>
    s === 'printed' ? 'default' :
    s === 'in_use'  ? 'success' :
    s === 'voided'  ? 'error'   :
    'default';

  const columns = useMemo<GridColDef<MachineQrPairSummary>[]>(() => [
    { field: 'qrId', headerName: 'QR id', flex: 1.2,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{p.row.qrId}</Typography>
        </Box>
      ) },
    { field: 'status', headerName: 'Status', width: 120,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip size="small" label={p.row.status} color={pairChipColour(p.row.status) as 'success' | 'error' | 'default'} />
        </Box>
      ) },
    // Asset number (human-readable), not the machine GUID — see the
    // "no raw GUIDs in UI" rule.
    { field: 'machineAssetNumber', headerName: 'Bound to machine (asset #)', flex: 1.4,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
            {p.row.machineAssetNumber ?? '—'}
          </Typography>
        </Box>
      ) },
    { field: 'createdAt', headerName: 'Created', width: 170,
      renderCell: (p) => <Typography variant="caption">{fmtDateTime(p.row.createdAt)}</Typography> },
    { field: 'voidedAt', headerName: 'Voided', width: 170,
      renderCell: (p) => <Typography variant="caption">{p.row.voidedAt ? fmtDateTime(p.row.voidedAt) : '—'}</Typography> },
  ], []);

  const quickFilters = useMemo<QuickFilter<MachineQrPairSummary>[]>(() => [
    { label: 'All',     filter: () => true },
    { label: 'Printed', filter: (p) => p.status === 'printed' },
    { label: 'In use',  filter: (p) => p.status === 'in_use' },
    { label: 'Voided',  filter: (p) => p.status === 'voided' },
  ], []);

  const exportColumns = useMemo<ExportColumn<MachineQrPairSummary>[]>(() => [
    { key: 'qrId',               label: 'QR id' },
    { key: 'status',             label: 'Status' },
    { key: 'machineAssetNumber', label: 'Machine asset #' },
    { key: 'machineId',          label: 'Machine id (internal)' },
    { key: 'createdAt',          label: 'Created',  format: (v) => fmtDateTime(v) },
    { key: 'voidedAt',           label: 'Voided',   format: (v) => fmtDateTime(v) },
  ], []);

  return (
    <Stack spacing={2}>
      <Button onClick={() => navigate('/factory/machine-qr-batches')} sx={{ alignSelf: 'flex-start' }}>
        ← Back to batches
      </Button>

      {batchQ.isLoading && <Typography>Loading…</Typography>}
      {batchQ.error && <ErrorAlert>Failed to load batch.</ErrorAlert>}

      {batch && (
        <>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={4} flexWrap="wrap" alignItems="center">
              <Stack>
                <Typography variant="overline" color="text.secondary">Batch code</Typography>
                <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>{batch.code}</Typography>
              </Stack>
              <Stack>
                <Typography variant="overline" color="text.secondary">Operator</Typography>
                <Typography variant="body1">{batch.intendedOperatorName ?? '—'}</Typography>
              </Stack>
              <Stack>
                <Typography variant="overline" color="text.secondary">Pairs</Typography>
                <Typography variant="h6">{batch.generatedCount.toLocaleString()}</Typography>
              </Stack>
              <Stack>
                <Typography variant="overline" color="text.secondary">Bound</Typography>
                <Typography variant="h6">{batch.boundCount.toLocaleString()} / {batch.generatedCount.toLocaleString()}</Typography>
              </Stack>
              <Stack>
                <Typography variant="overline" color="text.secondary">Status</Typography>
                <Chip size="small" label={batch.status} color={batchChipColour(batch.status) as 'success' | 'info' | 'error' | 'default'} />
              </Stack>
              <Stack>
                <Typography variant="overline" color="text.secondary">Created</Typography>
                <Typography variant="body2">{fmtDateTime(batch.createdAt)}</Typography>
              </Stack>
              {batch.printedAt && (
                <Stack>
                  <Typography variant="overline" color="text.secondary">First printed</Typography>
                  <Typography variant="body2">{fmtDateTime(batch.printedAt)}</Typography>
                </Stack>
              )}
              {batch.voidedAt && (
                <Stack>
                  <Typography variant="overline" color="text.secondary">Voided</Typography>
                  <Typography variant="body2">{fmtDateTime(batch.voidedAt)}</Typography>
                </Stack>
              )}
              <Box sx={{ flex: 1 }} />
            </Stack>
            {batch.notes && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{batch.notes}</Typography>
            )}
          </Card>

          {canPrint && (
            <Card variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Stack sx={{ flex: 1 }}>
                  <Typography variant="overline" color="text.secondary">Print</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Open the print preview to adjust paper / label / grid /
                    margins, print directly, or download the PDF for off-site
                    printing. Cabinet stickers are <strong>single</strong>
                    {' '}labels (no DEVICE+PACKAGING duplicate), so a {batch.generatedCount}-pair
                    batch produces {batch.generatedCount.toLocaleString()} labels.
                  </Typography>
                </Stack>
                <Button
                  variant="outlined" startIcon={<DownloadIcon />}
                  onClick={() => downloadCsv(batch.id, batch.code, alert)}
                >Download CSV</Button>
                <Button
                  variant="contained" startIcon={<PrintIcon />}
                  onClick={() => navigate(`/factory/machine-qr-batches/${batchId}/print`)}
                >Open print preview</Button>
              </Stack>
            </Card>
          )}

          <RouteDataGrid<MachineQrPairSummary>
            entityName="Machine QR pairs"
            queryKey={['machine-qr-batches', 'pairs', batchId] as readonly unknown[]}
            listFn={() => apiFetch<MachineQrPairSummary[]>(
              `/api/v1/factory/machine-qr-batches/${batchId}/qr-pairs`, {}, getToken)}
            columns={columns}
            searchFields={[{ kind: 'text', key: 'qrId', label: 'QR id' }]}
            quickFilters={quickFilters}
            exportColumns={exportColumns}
            reportTitle={`ARMS — ${batch.code} cabinet QR pairs`}
            emptyMessage="No QR pairs in this batch."
          />
        </>
      )}
    </Stack>
  );
}

async function downloadCsv(
  batchId: string, batchCode: string,
  alert: (opts: { title: string; message: string; severity?: 'info' | 'warning' | 'error' | 'success' }) => Promise<void>,
) {
  const url = `${apiBaseUrl}/api/v1/factory/machine-qr-batches/${batchId}/export.csv`;
  const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) {
    await alert({ title: 'Not signed in', message: 'Sign in and retry the download.', severity: 'warning' });
    return;
  }
  try {
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) {
      const text = await r.text();
      throw new Error(`HTTP ${r.status}: ${text || r.statusText}`);
    }
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `machine-qr-batch-${batchCode}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    await alert({ title: 'Download failed', message: (e as Error).message, severity: 'error' });
  }
}
